<template>
    <div class="account-details">
        <LeftBlob class="account-details__left-blob" />
        <RightBlob class="account-details__right-blob" />
        <div class="account-details__info">
            To start, we need some basic info for your account and study personalization.
        </div>
        <PocketInput
            v-model="firstName"
            class="account-details__firstName account-details__input"
            label="First Name"
            :aria-required="true"
            :error="errorFields.includes('firstName')"
            @keydown.enter="submitAccountDetails()"
        />
        <PocketInput
            v-model="lastName"
            class="account-details__lastName account-details__input"
            label="Last Name"
            :aria-required="true"
            :error="errorFields.includes('lastName')"
            @keydown.enter="submitAccountDetails()"
        />
        <div class="account-details__email-container">
            <PocketInput
                v-model="email"
                class="account-details__email account-details__input"
                label="Email"
                field-type="email"
                :aria-required="true"
                :error="errorFields.includes('email')"
                @keydown.enter="submitAccountDetails()"
            />
            <div class="account-details__email-tip">
                <span class="account-details__email-tip-title">
                    <img src="@/assets/onboarding/lock.png" alt="">
                    Your data is safe with us
                </span>
                <div class="account-details__email-tip-description">
                    We use email to create your account and to sync your data across devices.
                </div>
            </div>
        </div>
        <PocketInput
            v-if="createPassword"
            ref="passwordInput"
            v-model="password"
            :error="errorFields.includes('password')"
            class="account-details__input"
            :aria-required="true"
            label="Create Password"
            labelSubtext="10+ characters, including uppercase and a number"
            field-type="password"
            @keydown.enter="submitAccountDetails()"
        />
        <Errors
            v-if="errors.length"
            class="account-details__errors"
            :class="{ 'account-details__errors--password-errors': showPasswordErrorHeader }"
            :errors="errors"
            :error-header="errorsHeader"
        />
        <Errors
            v-if="activationError"
            class="account-details__errors"
            :class="{ 'account-details__errors--password-errors': showPasswordErrorHeader }"
            :errors="[ activationError ]"
            :error-header="errorsHeader"
        />
        <div class="account-details__create-password">
            <template v-if="!createPassword">
                <PocketButton
                    class="account-details__create-password-link"
                    type="tertiary-small"
                    @click="toggleCreatePassword"
                    @keydown.enter="toggleCreatePassword"
                    @mousedown.prevent
                >
                    Create a password
                </PocketButton>
                instead of using a link to sign in
            </template>
            <template v-else>
                <PocketButton
                    class="account-details__create-password-link"
                    type="tertiary-small"
                    @click="toggleCreatePassword"
                    @keydown.enter="toggleCreatePassword"
                    @mousedown.prevent
                >
                    Sign in with a link
                </PocketButton>
                instead of creating a password
            </template>
        </div>
        <div class="account-details__legal">
            <Checkbox
                v-model="agreed"
                class="account-details__legal-checkbox"
                :disabled="agreeDisabled"
                aria-label="I agree to the privacy policy and terms of service, required"
            />
            <div class="account-details__legal-text">
                I agree to the
                <a
                    target="_blank"
                    :href="`https://www.pocketprep.com/privacy-policy?${buildUTMParamString({
                        utm_campaign: 'app_settings',
                        utm_content: 'account_details_privacy_policy'
                    })}`"
                    class="account-details__legal-text-link"
                >privacy policy</a>
                and
                <a
                    target="_blank"
                    :href="`https://www.pocketprep.com/terms-of-service?${buildUTMParamString({
                        utm_campaign: 'app_settings',
                        utm_content: 'account_details_terms'
                    })}`"
                    class="account-details__legal-text-link"
                >terms of service</a>
            </div>
        </div>
        <PocketButton
            v-if="validGift"
            class="account-details__submit"
            :class="{ 'account-details__submit--gift': validGift && createPassword }"
            :disabled="
                !firstName.trim()
                    || !lastName.trim()
                    || !email
                    || !agreed
                    || (createPassword && !password)
                    || !!activationError"
            :is-loading="isLoading || isActivatingGift"
            @click="submitAccountDetails(true)"
            @keydown.enter="submitAccountDetails(true)"
        >
            Redeem Your Gift <Icon type="arrow" />
        </PocketButton>
        <PocketButton
            v-else
            class="account-details__submit"
            :class="{ 'account-details__submit--referral': validReferral && createPassword }"
            :disabled="!firstName.trim() || !lastName.trim() || !email || !agreed || (createPassword && !password)"
            :is-loading="isLoading"
            @click="submitAccountDetails()"
            @keydown.enter="submitAccountDetails()"
        >
            {{ exam.isFree ? 'Start Studying' : 'Choose Your Plan' }} <Icon type="arrow" />
        </PocketButton>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import { userModule } from '@/store/user/module'
import type { Study } from '@pocketprep/types'
import type { ClientExam } from '@/store/bundle/state'
import { isValidEmail, isValidPassword, passwordPolicyErrors } from '@/utils'
import LeftBlob from '@/assets/onboarding/LeftBlob.vue'
import RightBlob from '@/assets/onboarding/RightBlob.vue'
import { buildUTMParamString } from '@/store/utils'

const {
    Input: PocketInput,
    Button: PocketButton,
    Icon,
    Checkbox,
    Errors,
} = UIKit

const props = withDefaults(
    defineProps<{
        savedPassword: string
        exam: Study.Class.ExamMetadataJSON | ClientExam
        examVersions: (Study.Class.ExamMetadataJSON | ClientExam)[]
        validReferral?: boolean
        validGift?: boolean
        activationError?: string
        isActivatingGift?: boolean
        firstNameQuery?: string | null
        lastNameQuery?: string | null
        emailQuery?: string | null
    }>(),
    {
        validReferral: false,
        validGift: false,
        activationError: '',
        isActivatingGift: false,
        firstNameQuery: null,
        lastNameQuery: null,
        emailQuery: null,

    })

const emit = defineEmits<{
    submit: [submitInfo: { password: string; redeemGift: boolean }]
    clearActivationError: []
}>()

const errors = ref<string[]>([])
const errorFields = ref<string[]>([])
const errorsHeader = ref('')
const showPasswordErrorHeader = ref(false)
const firstName = ref('')
const lastName = ref('')
const email = ref('')
const agreed = ref(false)
const password = ref('')
const createPassword = ref(false)
const agreeDisabled = ref(false)
const isLoading = ref(false)
const passwordInput = ref<{ $el?: HTMLElement } | null>(null)

const sortedMajorExamVersion = computed(() => {
    const versions = props.examVersions
    return versions.sort((a, b) => -a.version.localeCompare(b.version, undefined, { numeric: true }))
})

// This is the full exam object not just the exam's version
const newestMajorVersionExam = computed(() => {
    return sortedMajorExamVersion.value[0]
})

const hasSelectedNewestMajorVersion = computed(() => {
    return props.exam.version.split('.')[0] === newestMajorVersionExam.value.version.split('.')[0]

})

onMounted(() => {
    const registeredUser = userModule.state.user
    firstName.value = registeredUser?.firstName || props.firstNameQuery || ''
    lastName.value = registeredUser?.lastName || props.lastNameQuery || ''
    email.value = registeredUser?.email || props.emailQuery || ''
    agreed.value = !!registeredUser
    agreeDisabled.value = !!registeredUser
    password.value = props.savedPassword
    createPassword.value = !!password.value
})

const toggleCreatePassword = () => {
    createPassword.value = !createPassword.value

    if (!createPassword.value) {
        password.value = ''
        errorFields.value = []
        errors.value = []
        errorsHeader.value = ''
        showPasswordErrorHeader.value = false
    } else {
        setTimeout(() => {
            const passwordInputRef = passwordInput.value
            const passwordInputEl = (passwordInputRef
                && '$el' in passwordInputRef
                && passwordInputRef.$el) as HTMLElement
            const inputEl = passwordInputEl?.querySelector('input')
            inputEl?.focus()
        }, 1)
    }
}

const updateWebConfigMessageSeen = async () => {
    if (!props.exam.minorVersionMessaging && hasSelectedNewestMajorVersion.value) {
        return
    }

    if (!hasSelectedNewestMajorVersion.value) {
        await userModule.actions.markNextMajorVersionSeen(newestMajorVersionExam.value)
    }

    if (props.exam.minorVersionMessaging) {
        const examMinorVersionMessage = props.exam.minorVersionMessaging?.type
        const typeOfMinorMessage = examMinorVersionMessage === 'newQuestions'
            ? 'minorNewQuestions'
            : 'minorNewMockExams'
        const hasSeenMinorExamVersionMessage =
            userModule.getters.getHasSeenMessage(typeOfMinorMessage, props.exam)

        if (!hasSeenMinorExamVersionMessage) {
            await userModule.actions.markMessageSeen(typeOfMinorMessage)
        }
    }
}

const submitAccountDetails = async (redeemGift = false) => {
    errors.value = []
    errorFields.value = []
    errorsHeader.value = ''
    showPasswordErrorHeader.value = false
    emit('clearActivationError')
    isLoading.value = true

    try {
        const currentUser = userModule.state.user
        if (!firstName.value || !lastName.value) {
            if (!firstName.value) {
                errorFields.value.push('firstName')
            }
            if (!lastName.value) {
                errorFields.value.push('lastName')
            }
            errors.value.push('Please enter a first and last name.')
        }

        if (!agreed.value) {
            errors.value.push('You must agree to the terms of service and privacy policy to continue.')
        }

        if (!isValidEmail(email.value)) {
            errorFields.value.push('email')
            errors.value.push('Invalid email address.')
        }

        if (createPassword.value && !isValidPassword(password.value)) {
            errorFields.value.push('password')
            showPasswordErrorHeader.value = errorFields.value.length === 1 && errorFields.value.includes('password')
            if (showPasswordErrorHeader.value) {
                errorsHeader.value = 'Password doesn`t meet the following requirements:'
            }

            const passwordErrors = passwordPolicyErrors(showPasswordErrorHeader.value, password.value)

            errors.value = [ ...errors.value, ...passwordErrors ]
        }

        if (!errors.value.length) {
            if (currentUser) {
                await userModule.actions.updateUser({
                    email: email.value,
                    firstName: firstName.value,
                    lastName: lastName.value,
                    password: password.value,
                    exam: props.exam,
                })
            } else {
                await userModule.actions.createUser({
                    email: email.value,
                    firstName: firstName.value,
                    lastName: lastName.value,
                    password: password.value,
                    exam: props.exam,
                })
            }

            await updateWebConfigMessageSeen()

            emitSubmit({
                password: password.value,
                redeemGift,
            })
        }
    } catch (err) {
        const error = (err as { error?: string }).error || (err as { message?: string }).message
        if (!err) {
            errors.value.push('Unable to create or update account.')
        } else if (error) {
            errors.value.push(error)
        } else if (err instanceof Error || typeof err === 'string') {
            errors.value.push(err.toString())
        }

    } finally {
        isLoading.value = false
    }
}

const emitSubmit = (submitInfo: { password: string; redeemGift: boolean }) => {
    emit('submit', submitInfo)
}
</script>
<style lang="scss" scoped>
.account-details {
    width: 320px;
    margin: 4px auto 0;

    &__left-blob {
        position: fixed;
        bottom: 0;
        left: 0;
        border-radius: 0 0 0 12px;

        @include breakpoint (black-bear) {
            display: none;
        }
    }

    &__right-blob {
        position: absolute;
        top: 0;
        right: 0;

        @include breakpoint (black-bear) {
            display: none;
        }
    }

    &__info {
        color: $slate-03;
        font-size: 14px;
        line-height: 19px;
        text-align: center;
        margin-bottom: 36px;
    }

    &__email-container {
        position: relative;
        color: $slate-03;
    }

    &__email-tip {
        position: absolute;
        width: 199px;
        right: -249px;
        top: 5px;
        line-height: 18px;
        font-size: 13px;

        @include breakpoint (brown-bear) {
            width: 171px;
            right: -189px;
            top: 10px;
        }

        @include breakpoint (black-bear) {
            position: relative;
            width: 262px;
            top: auto;
            right: auto;
            margin-bottom: 30px;
            margin-left: 3px;
        }

        &::before {
            content: '';
            position: absolute;
            left: -68px;
            top: -9px;
            background: url('@/assets/onboarding/tip-arrow.svg') no-repeat center center;
            width: 65px;
            height: 14px;
            transform: rotate(-10deg);

            @include breakpoint (black-bear) {
                display: none;
            }
        }
    }

    &__email-tip-title {
        margin-bottom: 13px;
        font-weight: 600;
        display: block;

        @include breakpoint (brown-bear) {
            margin-bottom: 8px;
        }

        img {
            width: 16px;
            display: inline-block;
            margin-right: 5px;
            vertical-align: top;
            margin-top: -2px;
        }
    }

    &__create-password {
        font-size: 13px;
        line-height: 18px;
        margin-bottom: 32px;
        margin-left: 11px;

        @include breakpoint (black-bear) {
            margin-bottom: 24px;
        }
    }

    &__create-password-link {
        font-weight: 500;
        font-size: 13px;
    }

    &__legal {
        font-size: 13px;
        line-height: 18px;
        margin-left: 4px;
        display: flex;
        align-items: center;
    }

    &__legal-checkbox {
        margin-right: 9px;
        vertical-align: middle;
        margin-bottom: 2px;
    }

    &__legal-text-link {
        color: $brand-blue;
        text-decoration: none;
    }

    &__input,
    &__errors {
        margin-bottom: 17px;
    }

    &__errors {
        &--password-errors {
            :deep(.uikit-errors__error) {
                &::before {
                    content: '•';
                    display: inline-block;
                    margin-right: 5px;
                }
            }
        }
    }

    &__submit {
        margin: 42px auto 42px;
        display: block;

        &--referral {
            margin-bottom: 16px;
        }

        &--gift {
            margin-bottom: 32px;
        }

        svg {
            margin-left: 10px;
        }
    }
}
</style>
